/**
 * Store Preview Merchandising Logic
 * 
 * Centralized product selection rules for the homepage store preview.
 * Used by both the prefetch hook and the UI component to ensure consistency.
 */

interface ProductNode {
  node: {
    handle: string;
    [key: string]: unknown;
  };
}

interface MerchandisingOptions {
  excludedHandles?: string[];
  prioritizedHandles?: string[];
  limit?: number;
}

const DEFAULT_EXCLUDED_HANDLES = ['forms-booklet'];
// Desired homepage highlight order:
// 1) Large: NEW Streetwear
// 2) Top-right: Catalogue
// 3) Bottom-right: E-Commerce (formerly Packshot)
const DEFAULT_PRIORITIZED_HANDLES = [
  'streetwear-editorial-look-book-premium',
  'chimera-prompt-os-catalogue-lookbook',
  'packshot-look-book',
];
const DEFAULT_LIMIT = 3;

/**
 * Selects products for the store preview section with merchandising rules applied.
 * - Excludes specified products (e.g., Forms Booklet)
 * - Prioritizes specified products (e.g., E-Commerce Look Book)
 * - Returns the specified limit of products
 */
export function selectStorePreviewProducts<T extends ProductNode>(
  products: T[],
  options: MerchandisingOptions = {}
): T[] {
  const {
    excludedHandles = DEFAULT_EXCLUDED_HANDLES,
    prioritizedHandles = DEFAULT_PRIORITIZED_HANDLES,
    limit = DEFAULT_LIMIT,
  } = options;

  // Filter out excluded products
  const filteredProducts = products.filter(
    (product) => !excludedHandles.includes(product.node.handle)
  );

  // Build ordered list: pinned (in explicit order) then fill with remaining
  const pinnedInOrder: T[] = [];
  const pinnedSet = new Set<string>();

  for (const handle of prioritizedHandles) {
    const match = filteredProducts.find((p) => p.node.handle === handle);
    if (match && !pinnedSet.has(handle)) {
      pinnedInOrder.push(match);
      pinnedSet.add(handle);
    }
  }

  const remaining = filteredProducts.filter((p) => !pinnedSet.has(p.node.handle));
  return [...pinnedInOrder, ...remaining].slice(0, limit);
}

/** Query key for store preview products - versioned to bust stale cache */
export const STORE_PREVIEW_QUERY_KEY = ["shopify-products-preview", "v2"] as const;
